import gsap from 'gsap';
import sounds from 'src/lib/sounds';
import { ReflowReason, View, ViewOpts } from '@play-co/timestep-core/ui';
import { TeamCompetitionButton } from '../minigame/match2-timestep/team/components/TeamCompetitionButton';
import StateObserver, { now, user } from 'src/StateObserver';
import { Feature } from 'src/replicant/analytics';
import { isMatch2DailyBonusActive } from 'src/replicant/match2/match2.getters';
import { openMatch2DailyBonusPopup } from '../minigame/match2-timestep/dailybonus/match2-dailybonus.sequence';
import features from 'src/replicant/ruleset/features';
import { openRaceSequence } from '../minigame/match2-timestep/races/races.sequence';
import { openStreakBattleRoyalePopup } from '../streakbattleroyale/streakbattleroyale.sequence';
import { createEmitter } from 'src/lib/Emitter';
import * as Match2 from 'src/replicant/match2';
import {
  grantMatch2DailyChallengeRewards,
  openMatch2DailyChallengePopup,
} from '../minigame/match2-timestep/dailychallenge/match2-dailychallenge.sequence';
import { openMatch2TournamentPopup } from '../minigame/match2-timestep/tournament/match2-tournament.sequence';
import { match2BundleSaleIsActive } from 'src/replicant/match2/bundlesale/match2-bundlesale.getters';
import { match2BundleSaleOpenPopup } from '../minigame/match2-timestep/bundlesale/match2-bundlesale.utils';
import { magicLampsOpen } from '../minigame/match2-timestep/magiclamps/magiclamps.sequence';
import { openLeagueLeaderboard } from '../minigame/match2-timestep/leagues/league.sequences';
import { openTeamEventPopup } from '../minigame/match2-timestep/team/team.sequence';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { FlexBoxView, FlexBoxViewOpts } from '@play-co/timestep-core/flexbox';
import { SceneID } from 'src/state/ui';
import { DaycareSideButton } from '../daycare/DaycareSideButton';
import * as Fate from 'src/replicant/fate';
import statePromise from 'src/lib/statePromise';
import {
  isNativeAppAvailable,
  isNativePromoAvailable,
} from '../native/native.util';
import NativeAppPromoButton from '../native/NativeAppPromoButton';
import { CardsButton } from '../cards/components/CardsButton';
import { cardsIsLocked } from 'src/replicant/cards/cards.getters';
import { cardsOpenLockedPopup, cardsOpenPopup } from '../cards/cards.sequence';
import { isPaymentAvailable } from 'src/lib/iap/iap.util';
import { DailyBonusButtonAnimated } from '../minigame/match2-timestep/components/DailyBonusButtonAnimated';
import { Match2TournamentButtonAnimated } from '../minigame/match2-timestep/tournament/Match2TournamentButtonAnimated';
import { RacesButtonAnimated } from '../minigame/match2-timestep/races/components/RacesButtonAnimated';
import { MagicLampsButtonAnimated } from '../minigame/match2-timestep/magiclamps/MagicLampsButtonAnimated';
import { Match2BundleSaleButtonAnimated } from '../minigame/match2-timestep/bundlesale/Match2BundleSaleButtonAnimated';
import { Match2DailyChallengeButtonAnimated } from '../minigame/match2-timestep/dailychallenge/Match2DailyChallengeButtonAnimated';
import { StarterPackButton } from '../starterpack/components/StarterPackButton';
import {
  isPetStarterPackAvailable,
  showStarterPackPromoFromHomeButton,
} from '../starterpack/starterpack.utils';
import { SuperLollipopButton } from '../superLollipop/components/SuperLollipopButton';
import { isDecorationGachaEventAvailable } from '../decoration/helpers';
import { openSuperLollipop } from '../superLollipop/superLollipop.sequence';
import { StreakBattleRoyaleButtonAnimated } from '../streakbattleroyale/StreakBattleRoyaleButtonAnimated';
import { scaleToFitParent } from '../../lib/ui/utils/scaleToFitParent';
import { GachaButton } from '../decoration/GachaButton';
import { WeeklyLeaderboardButton } from '../weeklyLeaderboard/components/WeeklyLeaderboardButton';
import { isWeeklyLeaderboardsUnlocked } from 'src/replicant/weeklyLeaderboard/weeklyLeaderboard.getters';
import {
  openGuestWeeklyLeaderboard,
  openWeeklyLeaderboard,
} from '../weeklyLeaderboard/weeklyLeaderboard.sequence';
import { TreasureExpressHomeButton } from '../treasureExpress/components/TreasureExpressHomeButton';
import { openTreasureExpress } from '../treasureExpress/treasureExpress.sequence';
import { ChampionsLeagueButton } from '../championsLeague/components/ChampionsLeagueButton';
import { canSeeArena } from 'src/replicant/championsLeague/championsLeague.getters';
import { JankenHudButton } from '../janken/components/JankenHudButton';
import { jankenHudSequence } from '../janken/janken.sequence';
import { homeShortcutButtonAnimated } from '../homeShortcut/components/HomeShortcutButtonAnimated';
import { openHomeShortcutPopup } from '../homeShortcut/homeShortcut.sequence';
import { isGuestUser } from 'src/replicant/guest/guest.getters';
import {
  buddiesIsEnabled,
  buddiesIsLocked,
} from 'src/replicant/buddies/buddies.getters';
import { BuddiesButton } from '../buddies/components/BuddiesButton';
import {
  buddiesOpenLockedPopup,
  buddiesOpenMainPopup,
} from '../buddies/buddies.sequence';
import { gachaTheme } from '../gacha/gacha.theme';
import { DreamDriftHudButton } from '../dreamDrift/components/DreamDriftHudButton';
import { dreamDriftOpen } from '../dreamDrift/dreamDrift.sequence';

type FloatingButton = View & {
  autoUpdate?: () => void;
  stopAutoUpdate?: () => void;
  instantUpdate?: () => void;
};

type HomeButtonsOpts = ViewOpts & { scene: SceneID };

export class HomeButtonsAnimated extends FlexBoxView {
  private scene: SceneID;
  private left: FlexBoxView;
  private right: FlexBoxView;
  private initialized = false;

  constructor(opts: HomeButtonsOpts) {
    super({
      flex: {
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      ...opts,
    });
    this.scene = opts.scene;

    const flexBoxOpts = {
      superview: this,
      width: 192,
      height: this.style.height,
      x: 0,
      flex: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
      },
      canHandleEvents: false,
    } satisfies FlexBoxViewOpts;

    this.left = new FlexBoxView(flexBoxOpts);
    this.right = new FlexBoxView(flexBoxOpts);
  }

  public reflow(reason: ReflowReason): void {
    super.reflow(reason);

    const leftHeight =
      canSeeArena(user(), now()) &&
      Fate.getters.getPermanentStatusEffectEndTime(user(), 'daycare', now())
        ? this.style.height - 190
        : this.style.height;
    this.left.updateOpts({
      height: leftHeight,
      flex: {
        height: leftHeight,
      },
    });

    // The Gacha button’s height can differ between themes.
    // Read the height from the theme and subtract it from the right-side height
    // so the Gacha button doesn’t overlap the other buttons.
    const locale = StateObserver.getLocale();
    const gachaButtonHeight =
      gachaTheme().getStyle(`gacha_button_${locale}`)?.height ?? 205;

    const rightHeight = isDecorationGachaEventAvailable()
      ? this.style.height - gachaButtonHeight - 20
      : this.style.height;
    this.right.updateOpts({
      height: rightHeight,
      flex: {
        height: rightHeight,
      },
    });
  }

  private init() {
    if (this.initialized) return;
    this.initialized = true;
    this.createButtons();

    this.getAllButtons().forEach((button) => {
      scaleToFitParent(button);
    });

    // Lock buttons if the menu is locked
    createEmitter(this, ({ ui }) => ui.lockMenu).addListener((lock) => {
      this.getAllButtons().forEach((button) => {
        button.updateOpts({ canHandleEvents: !lock });
      });
    });
  }

  public showScene() {
    if (!this.initialized) this.init();
    this.getAllButtons().forEach((button) => {
      if (button.autoUpdate) button.autoUpdate();
    });
  }

  public hideScene() {
    this.getAllButtons().forEach((button) => {
      if (button.stopAutoUpdate) button.stopAutoUpdate();
    });
  }

  public async hideAnimated() {
    this.getAllButtons().forEach((button) => {
      button.updateOpts({ canHandleEvents: false });
    });

    await Promise.all([
      gsap.to(this.left.style, { offsetX: -192, duration: 0.2 }),
      gsap.to(this.right.style, { offsetX: 192, duration: 0.2 }),
    ]);

    this.hide();
  }

  public async showAnimated() {
    this.show();

    await Promise.all([
      gsap.to(this.left.style, { offsetX: 0, duration: 0.2 }),
      gsap.to(this.right.style, { offsetX: 0, duration: 0.2 }),
    ]);

    this.getAllButtons().forEach((button) => {
      button.updateOpts({ canHandleEvents: true });
    });
  }

  private getLeftButtons() {
    return this.left.getSubviews() as FloatingButton[];
  }

  private getRightButtons() {
    return this.right.getSubviews() as FloatingButton[];
  }

  public getAllButtons() {
    return [
      ...this.getLeftButtons(),
      ...this.getRightButtons(),
    ] as FloatingButton[];
  }

  public getButton(tag: string) {
    return this.getAllButtons().find((button) => button.tag === tag);
  }

  private createButtons() {
    // LEFT
    this.createDaycareButton(this.left);
    this.createNativeAppButton(this.left);
    this.createMatch2DailyChallengeButton(this.left);
    this.createMatch2TournamentButton(this.left);
    this.createBundleSaleButton(this.left);
    this.createMagicLampsButton(this.left);
    this.createLeaguesButton(this.left);
    this.createTeamCompetitionButton(this.left);
    this.createCardsButton(this.left);
    this.createStarterPackButton(this.left);
    this.createBuddiesButton(this.left);
    this.createTreasureExpressButton(this.left);
    // RIGHT
    this.createDailyBonusButton(this.right);
    this.createJankenButton(this.right);
    this.createDreamDriftButton(this.right);
    this.createRacesButton(this.right);
    this.createStreakBattleRoyaleButton(this.right);
    this.createSuperLollipopButton(this.right);
    this.createWeeklyLeaderboardButton(this.right);
    this.createHomeShortcutButton(this.right);
    // Standalone
    this.addSubview(new GachaButton());
  }

  private createDaycareButton(parent: View) {
    // This button can only be shown on living room or arena.
    // If the scene is neither of those, we don't need to add the button.
    if (this.scene !== 'livingRoom' && this.scene !== 'arena') return;

    const daycareButton = new DaycareSideButton();
    parent.addSubview(daycareButton);

    createEmitter(daycareButton, ({ user }) => {
      const now = StateObserver.now();
      const daycareEndTime =
        Fate.getters.getPermanentStatusEffectEndTime(user, 'daycare', now) ?? 0;
      return daycareEndTime > now ? daycareEndTime : null;
    }).addListener((daycareEndTime) => {
      if (daycareEndTime) {
        void daycareButton.fadeIn();
        daycareButton.updateCountdownTimer(daycareEndTime);
      } else {
        daycareButton.fadeOut();
      }
    });
  }

  private createMatch2DailyChallengeButton(parent: View) {
    new Match2DailyChallengeButtonAnimated({
      superview: parent,
      tag: 'dailyChallenge',
      tracking: {
        feature: Feature.MATCH2.DAILY_CHALLENGE._,
        $subFeature: Feature.MATCH2.DAILY_CHALLENGE.BUTTON,
      },
      onClick: async () => {
        if (Match2.dailyChallengeGetters.isDailyChallengeComplete(user())) {
          await grantMatch2DailyChallengeRewards('hud');
        } else await openMatch2DailyChallengePopup('hud');
      },
    });
  }

  private createMatch2TournamentButton(parent: View) {
    new Match2TournamentButtonAnimated({
      tag: 'tournament',
      superview: parent,
      tracking: {
        feature: Feature.MATCH2.TOURNAMENT._,
        $subFeature: Feature.MATCH2.TOURNAMENT.BUTTON,
      },
      onClick: async () => {
        await openMatch2TournamentPopup('hud');
      },
    });
  }

  private createBundleSaleButton(parent: View) {
    if (
      !match2BundleSaleIsActive(user()) ||
      !isPaymentAvailable(StateObserver.getState())
    )
      return;
    const button = new Match2BundleSaleButtonAnimated({
      superview: parent,
      tracking: {
        feature: Feature.MATCH2.BUNDLE_SALE._,
        $subFeature: Feature.MATCH2.BUNDLE_SALE.SIDE_BUTTON,
      },
      onClick: () => match2BundleSaleOpenPopup('hud'),
    });
  }

  private createMagicLampsButton(parent: View) {
    new MagicLampsButtonAnimated({
      superview: parent,
      tracking: {
        feature: Feature.MATCH2.MAGIC_LAMPS._,
        $subFeature: Feature.MATCH2.MAGIC_LAMPS.SIDE_BUTTON,
      },
      onClick: () => magicLampsOpen('hud'),
    });
  }

  private createLeaguesButton(parent: View) {
    // No need to have this button in arena scene
    if (this.scene === 'arena') return;
    // Create the champions league button
    new ChampionsLeagueButton({
      superview: parent,
      tracking: {
        feature: Feature.MATCH2.LEAGUES._,
        $subFeature: Feature.MATCH2.LEAGUES.SIDE_BUTTON,
      },
      onClick: () => openLeagueLeaderboard('hud'),
    });
  }

  private createTeamCompetitionButton(parent: View) {
    if (!features(user()).teamsTournaments) return undefined;
    const button = new TeamCompetitionButton({
      superview: parent,
      tag: 'teamEvent',
      tracking: {
        feature: Feature.MATCH2.TEAM.COMPETITION._,
        $subFeature: Feature.MATCH2.TEAM.COMPETITION.SIDE_BUTTON,
      },
    });
    // The logic inside the button will be deciding which event popup to open, not ideal
    button.onClick = () =>
      openTeamEventPopup({
        eventType: button.getTeamEventType(),
        origin: 'hud',
      });
  }

  private createNativeAppButton(parent: View) {
    // This button can only be shown on living room or arena.
    // If the scene is neither of those, we don't need to add the button.
    if (this.scene !== 'livingRoom' && this.scene !== 'arena') return;

    if (!isNativeAppAvailable()) return;

    // Skip if payments are already available (e.g. LINE MiniApp, native app)
    if (isPaymentAvailable(StateObserver.getState())) return;

    const nativeButton = new NativeAppPromoButton({
      superview: parent,
      visible: false,
    });

    statePromise(() => isNativePromoAvailable()).then(() => {
      nativeButton.show();
    });

    createEmitter(
      this,
      ({ ui }) => ui.flags.nativePromoSessionSeen,
    ).addListener((seen) => {
      if (seen) {
        void nativeButton.fadeOut();
      }
    });
  }

  private createDailyBonusButton(parent: View) {
    if (!isMatch2DailyBonusActive(user())) return;

    new DailyBonusButtonAnimated({
      superview: parent,
      tracking: {
        feature: Feature.MATCH2.DAILY_BONUS._,
        $subFeature: Feature.MATCH2.DAILY_BONUS.BUTTON,
      },
      onClick: () => openMatch2DailyBonusPopup(),
    });
  }

  private createRacesButton(parent: View) {
    new RacesButtonAnimated({
      superview: parent,
      tag: 'race',
      tracking: {
        feature: Feature.MATCH2.RACES._,
        $subFeature: Feature.MATCH2.RACES.SIDE_BUTTON,
      },
      onClick: () => openRaceSequence({ originFeature: 'hud' }),
    });
  }

  private createStreakBattleRoyaleButton(parent: View) {
    new StreakBattleRoyaleButtonAnimated({
      superview: parent,
      tag: 'streakBattleRoyale',
      tracking: {
        feature: Feature.MATCH2.STREAK_EVENT._,
        $subFeature: Feature.MATCH2.STREAK_EVENT.SIDE_BUTTON,
      },
      onClick: async () => {
        await openStreakBattleRoyalePopup('hud');
      },
    });
  }

  private createCardsButton(parent: View) {
    new CardsButton({
      superview: parent,
      tag: 'cards',
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SIDE_BUTTON,
      },
      onClick: async () => {
        if (cardsIsLocked(user(), now())) {
          await cardsOpenLockedPopup('hud');
          return;
        }

        await cardsOpenPopup('hud');
      },
    });
  }

  private createStarterPackButton(parent: View) {
    if (!isPetStarterPackAvailable(user())) return;
    new StarterPackButton({
      superview: parent,
      tag: 'starterPack',
      tracking: {
        feature: Feature.PET_STARTER_PACK._,
        $subFeature: Feature.PET_STARTER_PACK.SIDE_BUTTON,
      },
      onClick: () => showStarterPackPromoFromHomeButton(),
    });
  }

  private createSuperLollipopButton(parent: View) {
    new SuperLollipopButton({
      superview: parent,
      tag: 'superLollipop',
      tracking: {
        feature: Feature.SUPER_LOLLIPOP._,
        $subFeature: Feature.SUPER_LOLLIPOP.BUTTON,
      },
      onClick: async () => {
        openSuperLollipop({ originFeature: 'hud' });
      },
    });
  }

  private createWeeklyLeaderboardButton(parent: View) {
    const { user } = StateObserver.getState();

    // Skip if weekly leaderboards are not enabled
    if (!features(user).weeklyLeaderboards) return;

    if (!isWeeklyLeaderboardsUnlocked(user)) return;

    // Will hide itself if the event is not available
    new WeeklyLeaderboardButton({
      superview: parent,
      tag: 'weeklyLeaderboard',
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.BUTTON,
      },
      onClick: async () =>
        isGuestUser(user)
          ? openGuestWeeklyLeaderboard('hud')
          : openWeeklyLeaderboard('hud'),
    });
  }

  private createTreasureExpressButton(parent: View) {
    new TreasureExpressHomeButton({
      superview: parent,
      tag: 'treasureExpress',
      tracking: {
        feature: Feature.TREASURE_EXPRESS._,
        $subFeature: Feature.TREASURE_EXPRESS.BUTTON,
      },
      onClick: async () => {
        await openTreasureExpress('hud');
      },
    });
  }

  private createJankenButton(parent: View) {
    if (!features(user()).petJanken) return;
    new JankenHudButton({
      superview: parent,
      tag: 'janken',
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.BUTTON,
      },
      onClick: async () => jankenHudSequence(),
    });
  }

  /**
   * Add the Dream Drift event entry to the home HUD.
   *
   * @param parent Right-side home-button collection that owns the entry.
   */
  private createDreamDriftButton(parent: View) {
    new DreamDriftHudButton({
      superview: parent,
      tag: 'dreamDrift',
      tracking: {
        feature: Feature.DREAM_DRIFT._,
        $subFeature: Feature.DREAM_DRIFT.BUTTON,
      },
      onClick: async () => {
        // Confirm the HUD entry tap before opening Dream Drift.
        sounds.sfx.play('ok');
        await dreamDriftOpen();
      },
    });
  }

  private createHomeShortcutButton(parent: View) {
    // Do not show home shortcut button when native promo is available (#9810)
    if (isNativePromoAvailable()) return;
    new homeShortcutButtonAnimated({
      superview: parent,
      tag: 'homeShortcut',
      tracking: {
        feature: Feature.HOME_SHORTCUT._,
        $subFeature: Feature.HOME_SHORTCUT.BUTTON,
      },
      onClick: async () => openHomeShortcutPopup('hud'),
    });
  }

  private createBuddiesButton(parent: View) {
    if (!buddiesIsEnabled(user())) return;
    new BuddiesButton({
      superview: parent,
      tag: 'buddies',
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_INFO,
      },
      onClick: async () => {
        if (buddiesIsLocked(user(), now())) {
          buddiesOpenLockedPopup('hud');
        } else {
          buddiesOpenMainPopup('hud');
        }
      },
    });
  }
}
